import * as stylex from '@stylexjs/stylex';
import clsx from 'clsx';
import { useId } from 'react';
import { getQTipData } from '../Tooltip/qTip.utilities';
import { controlStyles } from '../styles/controls';
import { colors } from '../theme/colors.stylex';
import { controlMetrics } from '../theme/controls.stylex';
import type { CheckboxProps } from './Checkbox.types';

const styles = stylex.create({
  root: { display: 'flex' },
  input: {
    boxSizing: 'border-box',
    appearance: 'none',
    padding: 0,
    height: '0.875rem',
    width: '0.875rem',
    flexShrink: 0,
    outlineStyle: 'none',
    boxShadow: 'none',
    backgroundColor: colors.fieldBackground,
    // SVG images are theme values: image documents cannot inherit the input color.
    backgroundImage: {
      'default': 'none',
      ':checked': { 'default': colors.checkboxImage, ':disabled': colors.checkboxDisabledImage },
    },
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
    backgroundSize: '100% 100%',
    backgroundOrigin: 'border-box',
    cursor: 'pointer',
  },
  checkbox: {
    borderRadius: controlMetrics.checkboxRadius,
    backgroundImage: {
      'default': 'none',
      ':checked': { 'default': colors.checkboxImage, ':disabled': colors.checkboxDisabledImage },
      ':indeterminate': {
        'default': colors.checkboxIndeterminateImage,
        ':disabled': colors.checkboxDisabledIndeterminateImage,
      },
    },
  },
  radio: { borderRadius: '9999px' },
  disabled: { cursor: 'not-allowed' },
  label: {
    marginLeft: '0.375rem',
    marginTop: '-0.125rem',
    fontSize: '0.875rem',
    lineHeight: '1.25rem',
    cursor: 'pointer',
    color: colors.text,
  },
  disabledLabel: { cursor: 'not-allowed', color: colors.textSubtle },
});

/**
 * Checkbox and Radio Box Component.
 */
export const Checkbox = (props: CheckboxProps) => {
  const {
    type = 'checkbox',
    value,
    disabled = false,
    label,
    onChange,
    onClick,
    onKeyDown,
    checked,
    defaultChecked,
    id,
    name,
    extraClassNames = '',
    extraLabelClassNames,
    xstyle,
    inputXstyle,
    labelXstyle,
    testId,
    ...tooltipProps
  } = props;

  const reactId = useId();
  const assignedId = id ?? `checkbox${reactId}`;
  const tooltipData = getQTipData(tooltipProps);

  const rootProps = stylex.props(styles.root, xstyle);
  const labelProps = stylex.props(styles.label, disabled && styles.disabledLabel, labelXstyle);
  return (
    <span {...rootProps} className={clsx(rootProps.className, extraClassNames)}>
      <input
        value={value}
        type={type}
        data-testid={testId}
        name={name}
        id={assignedId}
        readOnly={!onChange}
        checked={checked}
        defaultChecked={defaultChecked}
        {...stylex.props(
          controlStyles.base,
          styles.input,
          styles[type],
          disabled && controlStyles.disabled,
          disabled && styles.disabled,
          inputXstyle,
        )}
        disabled={disabled}
        onClick={onClick}
        onChange={onChange}
        onKeyDown={onKeyDown}
        {...tooltipData}
      />
      {label && (
        <label htmlFor={assignedId} {...labelProps} className={clsx(labelProps.className, extraLabelClassNames)}>
          {label}
        </label>
      )}
    </span>
  );
};
